import './CustomModal.css'

import React, { useEffect, useState } from 'react'
import { Overlay, Spinner, Tooltip } from 'react-bootstrap'

/**
 * CustomModal - A reusable modal component without Bootstrap dependencies
 *
 * @param {Object} props
 * @param {boolean} props.showModal - Controls modal visibility
 * @param {function} props.setShowModal - Function to update modal visibility
 * @param {function} props.confirmFunction - Function to execute on confirmation
 * @param {string} props.title - Modal title
 * @param {React.ReactNode} props.body - Modal body content (can be HTML)
 * @param {string} props.confirmBtnText - Text for the confirm button
 * @returns {JSX.Element}
 */
const CustomModalPReq = ({
  showModal,
  setShowModal,
  confirmFunction,
  title,
  body,
  confirmBtnText,
  modalLogo,
  badgeText,
  textArea = false,
}) => {
  const [isLoading, setIsLoading] = useState(false)
  const [modalStatus, setModalStatus] = useState('') // '', 'modal-open', or 'modal-closing'
  const [isVisible, setIsVisible] = useState(false)

  // Text Area
  const [tooltip, setTooltip] = useState('')
  const [target, setTarget] = useState(null)
  const [inputText, setinputText] = useState('')
  const [charCount, setCharCount] = useState(0)
  const charLimit = 500

  // Handle modal open/close animations
  useEffect(() => {
    if (showModal) {
      setIsVisible(true)
      setModalStatus('modal-open')
      document.body.style.overflow = 'hidden'
    } else if (isVisible) {
      setModalStatus('modal-closing')
      // Wait for animation to complete before removing from DOM
      const timer = setTimeout(() => {
        setIsVisible(false)
        document.body.style.overflow = 'auto'
      }, 300) // Match the animation duration
      return () => clearTimeout(timer)
    }

    return () => {
      document.body.style.overflow = 'auto'
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModal])

  const handleConfirm = async () => {
    setIsLoading(true)
    try {
      await confirmFunction()
    } catch (error) {
      console.error('Error in modal confirmation:', error)
    } finally {
      setIsLoading(false)
      setShowModal(false)
    }
  }

  const handleReject = () => {
    setShowModal(false)
  }

  const handleBackdropClick = e => {
    if (e.target.classList.contains('custom-modal-backdrop')) {
      setShowModal(false)
    }
  }

  const handleInput = e => {
    let value = e.target.value
    const invalidChars = /[^a-zA-Z0-9\s.,!?\-@#$%^&*()_+=\[\]{};:'"<>\/\\|`~]/g

    if (invalidChars.test(value)) {
      setTooltip('!אנגלית בלבד')
      e.target.value = value.replace(invalidChars, '')
      setTarget(e.target)
      setTimeout(() => {
        setTooltip('')
        setTarget(null)
      }, 2000)
      return
    }

    // Enforce character limit
    if (value.length > charLimit) {
      value = value.slice(0, charLimit)
    }

    setinputText(value)
    setCharCount(value.length)
  }

  if (!isVisible) return null

  return (
    <div className={`custom-modal-backdrop ${showModal ? 'show' : ''} ${modalStatus}`} onClick={handleBackdropClick}>
      <div className={`custom-modal ${showModal ? 'show' : ''} ${modalStatus}`}>
        <div className='custom-modal-header'>
          {/* <button className="custom-modal-close" onClick={() => setShowModal(false)}>
              &times;
            </button> */}
          <div className='custom-modal-handle'></div>
          <img src={modalLogo} alt='logo' className='custom-modal-logo' />

          <h3 className='custom-modal-title'>{title}</h3>
        </div>

        <div className='custom-modal-body'>
          {typeof body === 'string' ? <div dangerouslySetInnerHTML={{ __html: body }} /> : body}
          {textArea && (
            <div>
              <div className='custom-modal-text-area-container'>
                <div className='custom-modal-text-area-container-title'>הערות למחסן (אופציונלי)</div>
                <textarea className='custom-modal-text-area' id='shrink-input' onInput={handleInput} value={inputText}></textarea>
                <div className='custom-modal-text-area-info-count'>
                  {charCount}/{charLimit} תווים
                </div>
                <Overlay target={target} show={!!tooltip} placement='top'>
                  {props => (
                    <Tooltip id='overlay-tooltip' {...props}>
                      {tooltip}
                    </Tooltip>
                  )}
                </Overlay>
              </div>
              <div className='custom-modal-text-area-info'>אנא כתבו את ההערות באנגלית בלבד. ניתן להזין עד 500 תווים</div>
            </div>
          )}
        </div>

        <div className='custom-modal-badge'>{badgeText}</div>

        <div className='custom-modal-footer'>
          {isLoading ? (
            <div style={{ height: '77px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Spinner animation='border' variant='primary' />
            </div>
          ) : (
            <>
              <div className='custom-button confirm-button' onClick={handleConfirm} disabled={isLoading}>
                {confirmBtnText}
              </div>

              <div className='reject-button' onClick={handleReject} disabled={isLoading}>
                לא עכשיו
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default CustomModalPReq
